import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { AuthContext } from 'app/auth/contexts';
import Modal from 'app/common/components/Modal/Modal';
import { isSessionStorageExist } from 'app/common/utils/StorageUtils/StorageUtils';
import { useFormatMessage, useTenantContext } from 'app/common/hooks';
import messages from './PostLoginPopupManager.messages';
import { AuroraPayImageBlue } from 'app/checkout/utils/PaymentMethodUtil.js';
import { Button } from '../buttons/index.js';
import { get } from 'lodash';
import { Environment } from 'app/common/services';

/**
 * PostLoginPopupManager
 *
 * A lightweight controller component that watches authentication state and shows
 * a welcome popup exactly once per browser session after a successful login.
 *
 * Usage: Mount once near the root of the app (e.g., in BaseLayout).
 */
const SESSION_FLAG_KEY = 'postLoginPopupShown';
const CONTENT_PREFIX = '/api/asset/content/';

const PostLoginPopupManager = ({
  storageKey = SESSION_FLAG_KEY,
  renderContent
}) => {
  const { isAuthenticated, user } = React.useContext(AuthContext);
  const prevAuthRef = React.useRef(false);
  const [open, setOpen] = React.useState(false);
  const formatMessage = useFormatMessage();
  const tenantContext = useTenantContext();
  const isFleetPerformSite = useMemo(
    () =>
      auroraPayEnabled() &&
      get(tenantContext, 'application.fleetPerformEnabled', false),
    [tenantContext]
  );

  const pdfUrl = useMemo(() => {
    const params = {
      tenantId: get(tenantContext, 'tenant.id')
    };

    // allows for localizing the PDF file name if necessary
    const pdfName = formatMessage(messages.pdfFileName);
    const contextRequest = encodeURIComponent(JSON.stringify(params));
    return `${CONTENT_PREFIX}${pdfName}?contextRequest=${contextRequest}`;
  }, [tenantContext.tenant]);

  // Show only once per session
  const hasShownInSession = React.useMemo(() => {
    try {
      return (
        isSessionStorageExist() && sessionStorage.getItem(storageKey) === 'true'
      );
    } catch (e) {
      return true; // fail-safe: treat as already shown if storage is inaccessible
    }
  }, [storageKey]);

  React.useEffect(() => {
    if (
      !hasShownInSession &&
      isFleetPerformSite &&
      get(user, 'fleetPerformEnabled', false)
    ) {
      setOpen(true);
      try {
        if (isSessionStorageExist()) {
          sessionStorage.setItem(storageKey, 'true');
        }
      } catch (e) {
        // ignore storage errors
      }
    }

    prevAuthRef.current = isAuthenticated;
  }, [
    isAuthenticated,
    hasShownInSession,
    isFleetPerformSite,
    storageKey,
    user
  ]);

  const onClose = React.useCallback(() => setOpen(false), []);

  const content = renderContent ? (
    renderContent({ user, onClose })
  ) : (
    <>
      <Modal.Header>
        <Modal.CloseRed onClose={onClose} />
      </Modal.Header>
      <Modal.Body className={'flex flex-col items-center'}>
        <AuroraPayImageBlue className={'h-12 mx-auto'} />
        <p className={'my-8 text-xl w-4/5 md:w-3/5'}>
          {formatMessage(messages.fleetLoginModalText)}
        </p>
        <p className={'text-xl'}>{formatMessage(messages.learnMore)}</p>
        <Button className={'bg-aurora-500 text-white rounded mx-auto p-2'}>
          <a
            href={pdfUrl}
            className={'block text-xl mx-auto w-4/5'}
            target={'_blank'}
          >
            {formatMessage(messages.downloadButtonText)}
          </a>
        </Button>
      </Modal.Body>
    </>
  );

  return (
    <Modal isOpen={open} onClose={onClose} className={'login-success-modal'}>
      {content}
    </Modal>
  );
};

function auroraPayEnabled() {
  return Environment.get('AURORA_PAY_ENABLED', 'false') === 'true';
}

PostLoginPopupManager.propTypes = {
  /** override session storage key used to track if popup has been shown */
  storageKey: PropTypes.string,
  /** optional render prop to customize modal content */
  renderContent: PropTypes.func
};

export default PostLoginPopupManager;
export { PostLoginPopupManager };
